import MergeCellsOutlined from '@ant-design/icons/MergeCellsOutlined'
import { Button, ConfigProvider, Dropdown, Radio, Tooltip } from 'antd'
import { EXPLORER_BORDER_GRAY } from 'features/drilldown'
import React, { FC, ReactNode, useCallback } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { DrilldownPanelGroupType, DrilldownPanelType } from 'types'

import { useEnabledPanelsByGroup } from './panelConfigs/PanelConfigHooks'
import {
  DrilldownPanelConfig,
  DrilldownPanelInitialDataMap,
} from './panelConfigs/PanelConfigs'
import { BUTTON_GROUP_SPECS } from './panelConfigs/PanelGroupConfigs'

export type CreatePanelRequest<
  T extends DrilldownPanelType = DrilldownPanelType,
> = {
  panelType: T
  initialDataOverride?: DrilldownPanelInitialDataMap[T]
}

export interface CreatePanelButtonsProps {
  createPanels: (panelsToCreate: ReadonlyArray<CreatePanelRequest>) => void
  highlightType: DrilldownPanelGroupType | null
  onCollapseColumn?: () => void | undefined
}

const CreatePanelButtonsContainer = styled.div`
  width: 350px;
`

const StyledRadioButton = styled(Radio.Button)`
  &:hover {
    z-index: 10;
  }
`

const StyledDropdownButton = styled(Button)`
  .anticon {
    font-size: 16px !important;
  }
`

const RawCreatePanelButtons: FC<CreatePanelButtonsProps> = ({
  createPanels,
  highlightType,
  onCollapseColumn,
}) => {
  const buttonsByGroup = useEnabledPanelsByGroup()

  const renderButtonGroup = useCallback(
    ([groupType, buttonsInGroup]: [
      DrilldownPanelGroupType,
      DrilldownPanelConfig[],
    ]) => {
      const { tooltip: groupTooltip, icon } = BUTTON_GROUP_SPECS[groupType]

      // Make a dropdown if there are multiple buttons in the group
      if (buttonsInGroup.length > 1) {
        const dropdownMenuItems = buttonsInGroup.map((button) => ({
          key: button.name,
          onClick: () => createPanels([{ panelType: button.name }]),
          label: button.tooltip,
          icon: button.icon,
        }))

        return buildPanelButton(
          groupType as DrilldownPanelGroupType,
          () => {},
          groupTooltip ?? '',
          <Dropdown
            key={groupType}
            menu={{
              items: dropdownMenuItems,
            }}>
            <StyledDropdownButton type='text' icon={icon} />
          </Dropdown>,
          highlightType,
          true
        )
      }

      // For single-panel groups - no dropdown
      const button = buttonsInGroup[0]
      return buildPanelButton(
        button.name,
        () => createPanels([{ panelType: button.name }]),
        groupTooltip ?? button.tooltip,
        icon ?? button.icon,
        highlightType
      )
    },
    [createPanels, highlightType]
  )

  return (
    <CreatePanelButtonsContainer>
      <Radio.Group size='large'>
        {buttonsByGroup.map(renderButtonGroup)}
        {onCollapseColumn &&
          buildPanelButton(
            'Collapse Column',
            onCollapseColumn,
            'Collapse Column',
            <MergeCellsOutlined />,
            null
          )}
      </Radio.Group>
    </CreatePanelButtonsContainer>
  )
}
export const CreatePanelButtons = React.memo(RawCreatePanelButtons)

const buildPanelButton = (
  type: DrilldownPanelType | DrilldownPanelGroupType | string,
  handleClick: () => void,
  tooltip: ReactNode,
  buttonElement: ReactNode,
  highlightType: DrilldownPanelGroupType | null,
  shouldUseDropdownPadding: boolean = false
) => {
  const isHighlighted = type === highlightType

  return (
    <ConfigProvider
      key={type}
      theme={{
        components: {
          Radio: {
            colorPrimary: isHighlighted
              ? CantileverTheme.colors.highVis
              : EXPLORER_BORDER_GRAY,
            colorPrimaryHover: CantileverTheme.colors.highVis,
          },
        },
      }}>
      <Tooltip title={tooltip}>
        <StyledRadioButton
          style={{
            ...(shouldUseDropdownPadding
              ? { paddingLeft: '5px', paddingRight: '5px' }
              : {}),
            ...(isHighlighted ? { zIndex: 10 } : {}),
          }}
          onClick={handleClick}
          checked={isHighlighted}>
          {buttonElement}
        </StyledRadioButton>
      </Tooltip>
    </ConfigProvider>
  )
}
